<script lang="ts" setup>
import { computed, VNode } from 'vue';

import { InputVariant, IOptions } from '@/types';

interface Props {
  label?: string;
  placeholder?: string;
  labelIcon?: string | object | (() => VNode) | null;
  required?: boolean;
  variant?: InputVariant;
  modelValue?: string | number;
  options?: IOptions[];
  testId?: string;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
  labelIcon: null,
  disabled: false,
  required: false,
  variant: "primary",
  modelValue: "",
  options: () => [],
  testId: "select",
});

const variantClass = computed(() => props.variant ?? 'primary');
const backgroundStyle = computed(() => ({
  background:
    props.modelValue && ["primary", "admin"].includes(props.variant)
      ? "rgba(28, 86, 145, 0.05)"
      : "none",
}));
</script>

<template>
  <label :class="['w-100', variantClass]">
    <div v-if="label" class="select-label d-flex align-items-center">
      {{ label }}
      <span v-if="required">&ast;</span>
      <component v-if="labelIcon" :is="labelIcon" class="ms-1" />
    </div>
    <div class="select-wrapper position-relative">
      <select
        :style="{ ...backgroundStyle }"
        :value="modelValue"
        @change="e => $emit('update:modelValue', (e.target as HTMLSelectElement).value)"
        :data-testid="testId"
        v-bind="$attrs"
      >
        <option
          value=""
          disabled
          selected
          :style="{ color: 'inherit' }"
        >
          {{ placeholder }}
        </option>
        <option
          v-for="(item, index) in options"
          :key="index"
          :value="item.value"
        >
          {{ item.label }}
        </option>
      </select>
    </div>
  </label>
</template>

<style scoped>
label .select-label {
  margin-bottom: 0.25rem;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: capitalize;
}

label .select-wrapper {
  width: 100%;
  height: 2.5rem;
  max-height: 2.5rem;
}

label select {
  padding: 0.25rem 0.5rem;
  width: 100%;
  height: 100%;
  font-size: var(--font-sm);
  font-weight: 400;
  cursor: pointer;
}

label select:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, currentColor 10%, transparent);
  outline: none;
}

label select:disabled {
  color: rgba(0, 0, 0, 0.5);
  opacity: 0.75;
}

label select option:disabled {
  color: rgba(28, 86, 145, 0.2);
  font-size: var(--font-xs);
}

@media screen and (min-width: 1024px) {
  label .select-label {
    font-size: var(--font-sm);
  }
}

/* Variant Styles */

/* Primary Variant */
label.primary .select-label {
  color: var(--ch-black);
}

label.primary select {
  color: var(--ch-black);
  border: 1px solid var(--ch-blue-100);
  border-radius: 6px;
}

label.primary select:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 15%, transparent);
}

/* AdminV1 Variant */
label.admin .select-label {
  color: var(--ch-text-head);
}

label.admin select {
  color: var(--ch-text-head);
  border: 1px solid var(--ch-input);
  border-radius: 5px;
}

/* Grey Variant */
label.grey .select-label {
  color: var(--ch-black);
}

label.grey select {
  color: var(--ch-black);
  border: 1px solid var(--ch-border-grey-30);
  border-radius: 6px;
}

label.grey select::placeholder {
  color: var(--ch-border-grey);
}

label.grey select:focus {
  border: 2px solid var(--ch-blue-100);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 10%, transparent);
}

/* ReadOnly Variant */
label select[readonly]::placeholder {
  color: var(--ch-black) !important; 
  font-size: var(--font-sm) !important;
}

label select[readonly] { 
  border: 1px solid var(--ch-border-grey-30) !important; 
  border-radius: 6px !important;
  color: var(--ch-black) !important; 
  font-size: var(--font-sm) !important;
  background-color: var(--ch-readonly) !important;
}

label select[readonly]:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-readonly) 15%, transparent) !important;
  outline: none !important;
  border: 1px solid var(--ch-border-grey-30) !important;
}
</style>
